/*
 * Handles the "Invoke External Service" button click.
 *
 * 1. Reads Shared Context (token, orgId, locale, theme)
 * 2. Fetches user profile (displayName, email) from Adobe IMS
 * 3. Reads Editor State  (connections, selected items, editables, location)
 * 4. Fetches config from AEM placeholders to resolve the service URL & payload
 * 5. Calls the external service endpoint with the resolved data
 */

const DEFAULT_SERVICE_URL = process.env.DEFAULT_SERVICE_URL;

function buildPlaceholdersUrl(pageUrl) {
  const url = new URL(pageUrl);
  const aemOrigin = url.origin;
  const pathSegments = url.pathname.split("/").filter(Boolean);
  const siteName = pathSegments[1]; // pathname: /content/{site_name}/...
  return `${aemOrigin}/content/${siteName}/placeholders.hlx.json`;
}

async function fetchPlaceholders(placeholdersUrl, token) {
  const resp = await fetch(placeholdersUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`Placeholders fetch failed: ${resp.status}`);
  }
  const json = await resp.json();

  const lookup = {};
  for (const row of json.data || []) {
    lookup[row.Key] = row.Text;
  }

  return {
    externalServiceUrl: lookup["external-service-url"] || "DEFAULT",
    externalServicePayload: lookup["external-service-payload"] || "",
  };
}

async function invokeExternalService(guestConnection) {

  // ── 1. Shared Context ────────────────────────────────────────────────
  const sharedContext = guestConnection.sharedContext;
  const token = sharedContext.get("token");
  const orgId = sharedContext.get("orgId");
  const locale = sharedContext.get("locale");
  const theme = sharedContext.get("theme");
  const authScheme = sharedContext.get("authScheme");

  console.log("[InvokeExternalService] Shared context →", {
    token: token,
    orgId,
    locale,
    theme,
    authScheme,
    guestConnection,
  });

  // ── 2. User Profile ──────────────────────────────────────────────────
  let userName = "";
  let userEmail = "";
  try {
    const profileResponse = await fetch(
      "https://ims-na1.adobelogin.com/ims/profile/v1",
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (profileResponse.ok) {
      const profile = await profileResponse.json();
      userName = profile.displayName || "";
      userEmail = profile.email || "";
      console.log("[InvokeExternalService] User profile →", { userName, userEmail });
    }
  } catch (profileError) {
    console.warn("[InvokeExternalService] Failed to fetch user profile:", profileError);
  }

  // ── 3. Editor State ──────────────────────────────────────────────────
  const editorState = await guestConnection.host.editorState.get();
  const { connections, selected, editables, location } = editorState;

  console.log("[InvokeExternalService] Editor state →", {
    connections,
    selected,
    editablesCount: editables?.length,
    location,
    editorState,
  });

  // ── 4. Fetch config from AEM placeholders ──────────────────────────────
  let resolvedUrl = DEFAULT_SERVICE_URL;
  let resolvedPayload = {
    orgId,
    locale,
    theme,
    authScheme,
    "user-name": userName,
    "user-email": userEmail,
    aemPageUrl: location,
    connections,
    selected,
    editables,
  };

  try {
    const placeholdersUrl = buildPlaceholdersUrl(location);
    console.log("[InvokeExternalService] Fetching placeholders →", placeholdersUrl);

    const config = await fetchPlaceholders(placeholdersUrl, token);
    console.log("[InvokeExternalService] Placeholders config →", config);

    if (config.externalServiceUrl && config.externalServiceUrl !== "DEFAULT") {
      resolvedUrl = config.externalServiceUrl;
    }

    if (config.externalServicePayload) {
      resolvedPayload = {
        ...JSON.parse(config.externalServicePayload),
        aemPageUrl: location,
      };
    }
  } catch (configError) {
    console.warn(
      "[InvokeExternalService] Failed to load placeholders config, using defaults:",
      configError
    );
  }

  // ── 5. Call the external service ──────────────────────────────────────
  try {
    console.log("[InvokeExternalService] Calling service →", resolvedUrl);
    const headers = {
      "Content-Type": "application/json",
      "x-org-id": orgId,
    };
    const response = await fetch(resolvedUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(resolvedPayload),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(
        `[InvokeExternalService] Service responded with ${response.status}`,
        errorBody
      );
      throw new Error(
        `External service error: ${response.status} – ${errorBody}`
      );
    }

    const result = await response.json();
    console.log("[InvokeExternalService] Service result →", result);
    return result;
  } catch (error) {
    console.error("[InvokeExternalService] Failed to invoke service:", error);
    throw error;
  }
}

export default invokeExternalService;

